import type { HTMLAttributes, ReactNode } from 'react'

import { cn } from '../../lib/utils'
import { Icon, type IconName } from '../icon'

/**
 * A panel — the design's surface for a block of content.
 *
 * `panel` over `bg`, a 1px `line` border, 9px radius, and no shadow — the design shipped one and
 * the owner removed elevation from the product. This is a **bordered** card, which the journal
 * platform's rules
 * forbade; the console's own system says otherwise and it is the source here.
 *
 * The header divider is a border on the header rather than a floating element, for the same
 * reason: the design draws it that way, and a border is what a rounded container needs so the
 * line stops at the corner radius instead of running past it.
 */
export function Panel({
  title,
  description,
  actions,
  footer,
  className,
  children,
  ...props
}: {
  title?: ReactNode
  description?: ReactNode
  actions?: ReactNode
  footer?: ReactNode
} & HTMLAttributes<HTMLDivElement>) {
  return (
    <section
      className={cn(
        'bg-panel border-line overflow-hidden rounded-[9px] border',
        className,
      )}
      {...props}
    >
      {(title || actions) && (
        <header className="border-line flex items-start justify-between gap-4 border-b px-4 py-3">
          <div className="min-w-0">
            {title && <h2 className="text-text text-[13.5px] font-medium">{title}</h2>}
            {description && (
              <p className="text-muted mt-1 text-[12px] text-pretty">{description}</p>
            )}
          </div>
          {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
        </header>
      )}

      {children}

      {footer && <div className="border-line bg-panel2 border-t px-4 py-2.5">{footer}</div>}
    </section>
  )
}

/**
 * A label/value row inside a panel — the shape the design uses for every settings block and every
 * "what do we know about this thing" list.
 *
 * The label column is fixed so a stack of these forms a real column rather than a ragged edge.
 */
export function Row({
  label,
  children,
  labelWidth = 190,
}: {
  label: ReactNode
  children: ReactNode
  labelWidth?: number
}) {
  return (
    <div className="border-line2 flex gap-4 border-b px-4 py-3 text-[12.5px] last:border-b-0">
      <span className="text-muted shrink-0" style={{ width: labelWidth }}>
        {label}
      </span>
      <span className="text-text min-w-0 flex-1">{children}</span>
    </div>
  )
}

/**
 * The state a panel shows instead of content.
 *
 * One component for empty, error and denied, because they are the same shape and differ only in
 * what they say — and because writing them separately is how one of them ends up never written.
 */
export function Blank({
  icon = 'empty',
  tone = 'faint',
  title,
  body,
  action,
}: {
  /** Names the *kind* of nothing: empty, no results, denied, error, not wired. */
  icon?: IconName
  tone?: 'faint' | 'bad' | 'warn'
  title: ReactNode
  body?: ReactNode
  action?: ReactNode
}) {
  return (
    <div className="px-6 py-12 text-center">
      {/* The glyph says which kind of empty this is before the sentence is read — an empty list
          and a permission refusal are not the same event, and a heading alone makes them look it. */}
      <Icon
        name={icon}
        size={22}
        strokeWidth={1.5}
        className={cn(
          'mx-auto mb-3',
          tone === 'bad' ? 'text-bad' : tone === 'warn' ? 'text-warn' : 'text-faint',
        )}
      />
      <p className="text-text text-[13.5px]">{title}</p>
      {body && (
        <p className="text-muted mx-auto mt-1.5 max-w-[46ch] text-[12.5px] text-pretty">{body}</p>
      )}
      {action && <div className="mt-4 flex justify-center gap-2">{action}</div>}
    </div>
  )
}

/** A loading placeholder that shimmers rather than spins. */
export function Skeleton({ className }: { className?: string }) {
  return <div className={cn('bg-skel animate-shimmer rounded-[5px]', className)} />
}
